<template>
  <modal
    :show-footer="false"
    :show-top-dismiss-button="false"
    :max-width-str="state.maxWidthStr ?? undefined"
  >
    <template v-slot:header>
      <div class="image-wrapper-header">
        <span class="title">{{trimmedTitle}}</span>
        <div class="download-btn mr12" @click="downloadImage">
          <i class="fa fa-download"></i>
        </div>
        <div class="close-btn" @click="onClose">
          <i class="fas fa-times"> </i>
        </div>
      </div>
    </template>
    <template v-slot:body="slotProps">
      <div class="image-wrapper-body">
        <button
          v-if="imageUrls.length > 1"
          class="nav-btn nav-btn-prev"
          :disabled="state.currentIndex <= 0"
          aria-label="前の画像"
          @click="showPrevious"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <img
          ref="refImg1"
          class="img"
          :src="currentImageUrl"
          @load="onImageLoad"
          :style="{
            'max-height': (slotProps.modalBodyMaxHeight - 16 * 2) + 'px'
          }"
        />
        <button
          v-if="imageUrls.length > 1"
          class="nav-btn nav-btn-next"
          :disabled="state.currentIndex >= imageUrls.length - 1"
          aria-label="次の画像"
          @click="showNext"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import {
  computed,
  reactive,
  useTemplateRef,
  watch,
} from 'vue';
import { downloadObjectUrl } from 'src/lib/downloadHelper';
import { dtFormat } from 'src/lib/dateHelper';

interface ModalImageViewState {
  maxWidthStr: string | null;
  currentIndex: number;
}

interface Props {
  title?: string;
  imageUrls?: string[];
  downloadFilenameTemplate?: string;
  initialIndex?: number;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  imageUrls: () => [],
  downloadFilenameTemplate: '',
  initialIndex: 0,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const refImg1 = useTemplateRef<HTMLImageElement>('refImg1');
const state = reactive<ModalImageViewState>({
  maxWidthStr: null,
  currentIndex: props.initialIndex,
});

watch(
  [() => props.initialIndex, () => props.imageUrls],
  ([newIndex]) => {
    state.currentIndex = newIndex;
  },
);

const currentImageUrl = computed<string>(() => {
  return props.imageUrls[state.currentIndex] ?? '';
});

const onImageLoad = () => {
  // DOMの表示上のサイズを取得する
  const imgDispWidth = refImg1.value?.width || 0;
  const imgDispHeight = refImg1.value?.height || 0;
  if (imgDispWidth <= imgDispHeight) {
    state.maxWidthStr = null;
    return;
  }
  // 画像が横長の場合は横幅のmaxを広げる
  state.maxWidthStr = '75%';
};
const trimmedTitle = computed<string>(() => {
  const maxLen = state.maxWidthStr !== null ? 46 : 22;
  return props.title.length > maxLen
    ? `${props.title.slice(0, maxLen)}...`
    : props.title;
});
const downloadImage = () => {
  const timestamp = dtFormat(new Date(), 'yyyymmddHHMMSS');
  const downloadFilename = props.downloadFilenameTemplate.replace(/\$TIMESTAMP/, timestamp);
  downloadObjectUrl(currentImageUrl.value, downloadFilename);
};
const onClose = () => {
  emit('close');
};
const showPrevious = () => {
  if (state.currentIndex > 0) {
    state.maxWidthStr = null;
    state.currentIndex--;
  }
};
const showNext = () => {
  if (state.currentIndex < props.imageUrls.length - 1) {
    state.maxWidthStr = null;
    state.currentIndex++;
  }
};
</script>

<style lang="scss" scoped>
.download-btn {
  cursor: pointer;
  i {
    margin-top: 8px;
  }
}
.close-btn {
  font-size: 1.3em;
  cursor: pointer;
}
.image-wrapper-header {
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  .title {
    font-size: 1.4rem;
    flex-grow: 1;
  }
}
.image-wrapper-body {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .img {
    display: block;
    max-width: 100%;
    object-fit: contain;
  }

  .nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.4);
    border: none;
    color: white;
    font-size: 1.5rem;
    padding: 8px 14px;
    cursor: pointer;
    border-radius: 4px;
    z-index: 1;
    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
    &:not(:disabled):hover {
      background: rgba(0, 0, 0, 0.65);
    }
  }
  .nav-btn-prev {
    left: 8px;
  }
  .nav-btn-next {
    right: 8px;
  }
}
</style>
